import { useMemo } from 'react';
import { useReducedMotion } from 'motion/react';
import { pageEntrance } from '@/shared/constants/motion.constants';

/**
 * Entrance motion for a page shell that mounts a header above a content
 * surface. Returns the props to spread onto the two motion elements, so every
 * shell using it enters identically instead of each one picking its own
 * offsets and delays.
 *
 * Under `prefers-reduced-motion` both halves keep the fade and drop the
 * travel, which is the part that provokes symptoms.
 *
 * The props are memoized so a re-render doesn't hand motion a fresh identity
 * and restart the tween mid-flight.
 */
export function usePageEntrance() {
  const shouldReduceMotion = useReducedMotion();

  return useMemo(
    () => ({
      header: {
        initial: { opacity: 0, x: shouldReduceMotion ? 0 : pageEntrance.header.offsetX },
        animate: { opacity: 1, x: 0 },
        transition: {
          duration: pageEntrance.durationS,
          ease: 'easeOut' as const,
          delay: pageEntrance.header.delayS,
        },
      },
      body: {
        initial: { opacity: 0, y: shouldReduceMotion ? 0 : pageEntrance.body.offsetY },
        animate: { opacity: 1, y: 0 },
        transition: {
          duration: pageEntrance.durationS,
          ease: 'easeOut' as const,
          delay: pageEntrance.body.delayS,
        },
      },
    }),
    [shouldReduceMotion],
  );
}
